import React from 'react';
import { Typography, Card, Space, Button, Divider } from 'antd';
import { ArrowLeft, Shield, Eye, Lock, Database, UserCheck } from 'lucide-react';
import PageContainer from './PageContainer';

const { Title, Text, Paragraph } = Typography;

interface PrivacyPolicyProps {
  onBack?: () => void;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onBack }) => {
  return (
    <PageContainer>
      {/* 头部 */}
      <div style={{ marginBottom: 32 }}>
        {onBack && (
          <Button
            icon={<ArrowLeft size={16} />}
            onClick={onBack}
            style={{ marginBottom: 16 }}
          >
            返回
          </Button>
        )}
        <Title level={2}>
          <Shield size={24} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          隐私政策
        </Title>
        <Text type="secondary">
          最后更新时间：2025年7月12日
        </Text>
      </div>

      {/* 概述 */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={3}>概述</Title>
        <Paragraph>
          小酷笔记（以下简称"我们"）非常重视用户的隐私保护。
          本隐私政策详细说明了我们如何收集、使用、存储和保护您的个人信息。
          使用我们的服务即表示您同意本隐私政策的条款。
        </Paragraph>
      </Card>

      {/* 信息收集 */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={3}>
          <Database size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          我们收集的信息
        </Title>

        <Title level={4}>1. 账户信息</Title>
        <Paragraph>
          • <Text strong>注册信息：</Text>用户名、邮箱地址、密码（加密存储）
          <br />
          • <Text strong>个人资料：</Text>头像、个人设置偏好
        </Paragraph>

        <Title level={4}>2. 内容信息</Title>
        <Paragraph>
          • <Text strong>笔记内容：</Text>您创建、编辑和存储的所有笔记内容
          <br />
          • <Text strong>标签和分类：</Text>您为笔记设置的标签和分类信息
          <br />
          • <Text strong>AI 对话：</Text>与 AI 助手的对话记录（用于改进服务）
        </Paragraph>

        <Title level={4}>3. 使用数据</Title>
        <Paragraph>
          • <Text strong>访问日志：</Text>IP 地址、浏览器类型、访问时间
          <br />
          • <Text strong>使用统计：</Text>功能使用频率、错误报告
          <br />
          • <Text strong>设备信息：</Text>操作系统、屏幕分辨率（仅用于优化体验）
        </Paragraph>
      </Card>

      {/* 信息使用 */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={3}>
          <Eye size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          信息使用方式
        </Title>

        <Paragraph>
          我们使用收集的信息用于以下目的：
        </Paragraph>

        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text>• <Text strong>提供服务：</Text>存储和同步您的笔记内容</Text>
          <Text>• <Text strong>账户管理：</Text>身份验证、密码重置、账户安全</Text>
          <Text>• <Text strong>功能改进：</Text>分析使用模式，优化产品功能</Text>
          <Text>• <Text strong>AI 服务：</Text>提供智能助手功能和个性化建议</Text>
          <Text>• <Text strong>技术支持：</Text>解决技术问题，提供客户服务</Text>
          <Text>• <Text strong>安全保护：</Text>检测和防范安全威胁</Text>
        </Space>
      </Card>

      {/* 信息保护 */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={3}>
          <Lock size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          信息保护措施
        </Title>

        <Title level={4}>1. 技术保护</Title>
        <Paragraph>
          • <Text strong>数据加密：</Text>采用 HTTPS 和 AES-256 加密技术
          <br />
          • <Text strong>访问控制：</Text>严格的身份验证和权限管理
          <br />
          • <Text strong>安全审计：</Text>定期进行安全评估和漏洞扫描
        </Paragraph>

        <Title level={4}>2. 管理保护</Title>
        <Paragraph>
          • <Text strong>员工培训：</Text>所有员工签署保密协议并接受隐私培训
          <br />
          • <Text strong>最小权限：</Text>员工仅能访问工作必需的数据
          <br />
          • <Text strong>定期备份：</Text>多地备份确保数据安全和可恢复性
        </Paragraph>
      </Card>

      {/* 信息共享 */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={3}>信息共享和披露</Title>

        <Paragraph>
          我们承诺不会出售、出租或交易您的个人信息。
          仅在以下情况下可能共享您的信息：
        </Paragraph>

        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text>• <Text strong>法律要求：</Text>遵守法律法规、法院命令或政府要求</Text>
          <Text>• <Text strong>安全保护：</Text>保护用户、公众或我们的权利和安全</Text>
          <Text>• <Text strong>服务提供：</Text>与可信的第三方服务提供商（如云存储）</Text>
          <Text>• <Text strong>用户同意：</Text>获得您明确同意的其他情况</Text>
        </Space>
      </Card>

      {/* 用户权利 */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={3}>
          <UserCheck size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          您的权利
        </Title>

        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text>• <Text strong>访问权：</Text>查看我们持有的您的个人信息</Text>
          <Text>• <Text strong>更正权：</Text>更新或修正不准确的个人信息</Text>
          <Text>• <Text strong>删除权：</Text>要求删除您的个人信息和账户</Text>
          <Text>• <Text strong>导出权：</Text>以常用格式导出您的数据</Text>
          <Text>• <Text strong>限制权：</Text>限制我们处理您的个人信息</Text>
          <Text>• <Text strong>撤回权：</Text>撤回之前给予的同意</Text>
        </Space>

        <Divider />

        <Paragraph>
          如需行使上述权利，请通过 daily.name666.top 联系我们。
          我们将在 30 天内回复您的请求。
        </Paragraph>
      </Card>

      {/* Cookie 政策 */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={3}>Cookie 和类似技术</Title>

        <Paragraph>
          我们使用 Cookie 和类似技术来：
        </Paragraph>

        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text>• 保持您的登录状态</Text>
          <Text>• 记住您的偏好设置</Text>
          <Text>• 分析网站使用情况</Text>
          <Text>• 提供个性化体验</Text>
        </Space>

        <Paragraph style={{ marginTop: 16 }}>
          您可以通过浏览器设置管理 Cookie，但这可能影响某些功能的正常使用。
        </Paragraph>
      </Card>

      {/* 政策更新 */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={3}>政策更新</Title>

        <Paragraph>
          我们可能会不时更新本隐私政策。重大变更时，我们会通过以下方式通知您：
        </Paragraph>

        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text>• 在网站显著位置发布通知</Text>
          <Text>• 向您的注册邮箱发送通知</Text>
          <Text>• 在应用内推送通知</Text>
        </Space>

        <Paragraph style={{ marginTop: 16 }}>
          继续使用我们的服务即表示您接受更新后的隐私政策。
        </Paragraph>
      </Card>

      {/* 联系我们 */}
      <Card>
        <Title level={3}>联系我们</Title>

        <Paragraph>
          如果您对本隐私政策有任何疑问或建议，请通过以下方式联系我们：
        </Paragraph>

        <Space direction="vertical" size="small">
          <Text>• 邮箱：daily.name666.top</Text>
          <Text>• 地址：中国唐山市曹妃甸区渤海大道21号</Text>
          <Text>• 电话：15633971706</Text>
        </Space>

        <Divider />

        <Text type="secondary">
          本隐私政策的解释权归小酷笔记所有。
        </Text>
      </Card>
    </PageContainer>
  );
};

export default PrivacyPolicy;
